import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import PageShell, { SectionLabel } from "@/components/PageShell";
import { getBlogPosts } from "@/lib/api";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getBlogPosts()
      .then(setPosts)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <PageShell testId="page-blog">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 py-16 md:py-24">
          <div className="md:col-span-4">
            <SectionLabel index={1}>Insights</SectionLabel>
          </div>
          <div className="md:col-span-8">
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-white leading-[1.02] tracking-tight">
              Field notes from<br />
              <span className="text-zinc-500">the research floor.</span>
            </h1>
            <p className="mt-8 text-lg text-zinc-400 leading-relaxed max-w-2xl">
              Working papers, engineering deep-dives and opinionated takes from our team.
            </p>
          </div>
        </div>

        <div className="border-t border-white/5 pt-12 pb-24">
          {loading && (
            <p data-testid="blog-loading" className="font-mono text-sm text-zinc-500">
              Loading posts…
            </p>
          )}
          {error && (
            <p data-testid="blog-error" className="font-mono text-sm text-red-400">
              Failed to load posts.
            </p>
          )}
          <div data-testid="blog-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {posts.map((p, i) => (
              <motion.article
                key={p.id}
                data-testid={`blog-card-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.45, delay: i * 0.05 }}
                className="group bg-[#0C0C0D] border border-white/5 hover:border-[#CCFF00]/30 transition-colors duration-300"
              >
                <Link
                  to={`/blog/${p.slug || p.id}`}
                  data-testid={`blog-card-link-${i}`}
                  className="block"
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-[#050505]">
                    <img
                      src={p.image_url}
                      alt={p.title}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-[filter,transform] duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#CCFF00]">
                        {p.category}
                      </span>
                      <span className="font-mono text-[10px] text-zinc-600">{p.read_time}</span>
                    </div>
                    <h3 className="font-display text-xl text-white leading-snug group-hover:text-[#CCFF00] transition-colors duration-300">
                      {p.title}
                    </h3>
                    <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{p.excerpt}</p>
                    <div className="mt-6 flex items-center justify-between border-t border-white/5 pt-4">
                      <span className="font-mono text-[10px] text-zinc-500">{p.author}</span>
                      <ArrowUpRight size={14} className="text-zinc-500 group-hover:text-[#CCFF00] transition-colors duration-300" />
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </PageShell>
  );
}
